import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import SsoLogin from './pages/SsoLogin';
import BoardPage from './pages/BoardPage';
import NotificationBell from './components/NotificationBell';
import AccessibilityControl from './components/AccessibilityControl';
import TeamManagerModal from './components/TeamManagerModal';
import EmailTemplateManagerModal from './components/EmailTemplateManagerModal';
import ShippingProfileManagerModal from './components/ShippingProfileManagerModal';
import UnmatchedEmailQueueModal from './components/UnmatchedEmailQueueModal';
import CustomerEmailsModal from './components/CustomerEmailsModal';
import ComposeBroadcastEmailModal from './components/ComposeBroadcastEmailModal';
import MyPreferencesModal from './components/MyPreferencesModal';
import DailyReportModal from './components/DailyReportModal';
import WorkdayChip from './components/WorkdayChip';
import AttendanceModal from './components/AttendanceModal';
import DailyReportArchiveModal from './components/DailyReportArchiveModal';
import PriorityAlertBanner from './components/PriorityAlertBanner';
import PostAlertModal from './components/PostAlertModal';
import ChatModal from './components/ChatModal';
import * as api from './api/endpoints';
import { playNotificationSound } from './utils/sound';

// The "Daily report" button starts blinking once it's this hour or later
// (24h, local time) if the employee hasn't submitted today's report yet --
// a gentle nudge rather than an interruption. 16 = 4pm.
const DAILY_REPORT_REMINDER_HOUR = 16;

// How often the conversation list is re-checked in the background (i.e.
// even while the Chat modal is closed) to notice new messages -- drives
// the bottom-right toast popup and the blinking browser tab, Facebook/
// Messenger-style. Deliberately faster than the old unread-count-only
// poll (20s) so the "someone just messaged you" popup feels prompt.
const CHAT_LIST_POLL_MS = 8000;

const RequireAuth = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const Shell = ({ children }) => {
  const { employee, logout, updateEmployee } = useAuth();
  const navigate = useNavigate();
  const [showTeams, setShowTeams] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [showShippingProfiles, setShowShippingProfiles] = useState(false);
  const [showUnmatchedEmails, setShowUnmatchedEmails] = useState(false);
  const [showCustomerEmails, setShowCustomerEmails] = useState(false);
  const [showEmailsMenu, setShowEmailsMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [showMyPreferences, setShowMyPreferences] = useState(false);
  const [showDailyReport, setShowDailyReport] = useState(false);
  const [showDailyReportArchive, setShowDailyReportArchive] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [showPostAlert, setShowPostAlert] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [teams, setTeams] = useState([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [dailyReportSubmitted, setDailyReportSubmitted] = useState(true); // assume done until we hear otherwise, so the button never blinks on a slow load
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [jumpToConversationId, setJumpToConversationId] = useState(null);

  const refreshAlerts = () => api.getActiveAlerts().then(setActiveAlerts).catch(() => {});
  useEffect(() => {
    refreshAlerts();
    const interval = setInterval(refreshAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Tracks each conversation's last-seen unread_count so a poll-over-poll
  // *increase* can be told apart from "still has 3 unread from before" --
  // only a genuine increase should pop a toast / start the tab blinking.
  // Ref (not state) because it's read/written from inside the interval
  // callback and shouldn't itself trigger a re-render.
  const prevConvUnreadRef = useRef(null); // null until the first successful load
  const showChatRef = useRef(false);
  useEffect(() => { showChatRef.current = showChat; }, [showChat]);

  const originalTitleRef = useRef(document.title);
  const blinkIntervalRef = useRef(null);
  const stopTabBlink = () => {
    if (blinkIntervalRef.current) {
      clearInterval(blinkIntervalRef.current);
      blinkIntervalRef.current = null;
      document.title = originalTitleRef.current;
    }
  };
  const startTabBlink = (text) => {
    // Only worth blinking if they're not even looking at this tab right
    // now -- if it's visible/focused, the toast alone is enough.
    if (document.visibilityState === 'visible') return;
    if (blinkIntervalRef.current) return; // already blinking
    let showAlt = true;
    document.title = `\u{1F4AC} ${text}`;
    blinkIntervalRef.current = setInterval(() => {
      document.title = showAlt ? originalTitleRef.current : `\u{1F4AC} ${text}`;
      showAlt = !showAlt;
    }, 1000);
  };
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') stopTabBlink(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      stopTabBlink();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshChatUnreadCount = () => {
    api.getChats().then((conversations) => {
      const total = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setChatUnreadCount(total);

      const prev = prevConvUnreadRef.current;
      if (prev) {
        conversations.forEach((c) => {
          const before = prev[c.id] || 0;
          if (c.unread_count > before) {
            const who = c.is_group ? `#${c.name}` : c.name;
            const preview = c.last_message?.body ? c.last_message.body.slice(0, 100) : '';
            toast.info(
              <div onClick={() => { setJumpToConversationId(c.id); setShowChat(true); }} style={{ cursor: 'pointer' }}>
                <strong>{who}</strong>{preview ? <div style={{ fontSize: '0.82rem', marginTop: '0.15rem' }}>{preview}</div> : null}
              </div>,
              { icon: '\u{1F4AC}' },
            );
            if (!showChatRef.current) startTabBlink(`New message${c.is_group ? '' : ` from ${c.name}`}`);
            playNotificationSound();
          }
        });
      }
      prevConvUnreadRef.current = Object.fromEntries(conversations.map((c) => [c.id, c.unread_count || 0]));
    }).catch(() => {});
  };
  useEffect(() => {
    refreshChatUnreadCount();
    const interval = setInterval(refreshChatUnreadCount, CHAT_LIST_POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showTeams || showDailyReportArchive || showChat) api.getEmployees().then(setEmployees).catch(() => {});
  }, [showTeams, showDailyReportArchive, showChat]);

  useEffect(() => {
    if (showChat) api.getTeams().then(setTeams).catch(() => {});
  }, [showChat]);

  useEffect(() => {
    if (employee?.is_admin) {
      api.getUnmatchedEmails().then((items) => setUnmatchedCount(items.length)).catch(() => {});
    }
  }, [employee, showUnmatchedEmails]);

  // Refreshes whether today's report is in yet -- checked on load, again
  // whenever the form is closed (in case they just submitted), and every
  // few minutes in the background so the reminder blink can turn on by
  // itself if the tab's been open since before 4pm.
  useEffect(() => {
    const refresh = () => api.getDailyReportToday().then((data) => setDailyReportSubmitted(!!data.submitted)).catch(() => {});
    refresh();
    const interval = setInterval(refresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [showDailyReport]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  const dailyReportDue = !dailyReportSubmitted && now.getHours() >= DAILY_REPORT_REMINDER_HOUR;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <div className="topbar">
        <div className="topbar-left">
          <span className="topbar-title">Mavis Service Desk</span>
        </div>
        <div className="topbar-right">
          {employee?.is_admin && (
            <>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowEmailsMenu((v) => !v)}>
                  Emails{unmatchedCount > 0 ? ` (${unmatchedCount})` : ''} {'\u25BE'}
                </button>
                {showEmailsMenu && (
                  <>
                    <div onClick={() => setShowEmailsMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
                    <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.25rem', minWidth: 190, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', boxShadow: '0 6px 20px rgba(0,0,0,0.35)', zIndex: 901, overflow: 'hidden' }}>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowUnmatchedEmails(true); setShowEmailsMenu(false); }}>
                        Unlinked emails{unmatchedCount > 0 ? ` (${unmatchedCount})` : ''}
                      </button>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowComposeEmail(true); setShowEmailsMenu(false); }}>
                        Compose email
                      </button>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowCustomerEmails(true); setShowEmailsMenu(false); }}>
                        Customer emails
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button type="button" className="btn-secondary" onClick={() => setShowAttendance(true)}>Team attendance</button>
              <button type="button" className="btn-secondary" onClick={() => setShowDailyReportArchive(true)}>Daily report history</button>
              <button type="button" className="btn-secondary" onClick={() => setShowPostAlert(true)}>Post reminder</button>
            </>
          )}
          <WorkdayChip />
          <button
            type="button" className={`btn-secondary${dailyReportDue ? ' btn-blink' : ''}`}
            onClick={() => setShowDailyReport(true)}
            title={dailyReportDue ? "You haven't submitted today's report yet" : 'Daily report'}
          >
            Daily report
          </button>
          {!employee?.is_admin && (
            <button type="button" className="btn-secondary" onClick={() => setShowDailyReportArchive(true)}>My report history</button>
          )}
          <button
            type="button" className="notif-bell" onClick={() => setShowChat(true)}
            aria-label={`Chat${chatUnreadCount > 0 ? `, ${chatUnreadCount} unread` : ''}`} title="Chat"
          >
            <span aria-hidden="true" style={{ fontSize: '1.15rem', verticalAlign: 'middle', lineHeight: 1 }}>{'\u{1F4AC}'}</span>
            {chatUnreadCount > 0 && <span className="notif-badge">{chatUnreadCount > 9 ? '9+' : chatUnreadCount}</span>}
          </button>
          <NotificationBell />
          <AccessibilityControl />
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button" className="btn-secondary" title="Settings" aria-label="Settings"
              onClick={() => setShowSettingsMenu((v) => !v)}
            >
              ⚙
            </button>
            {showSettingsMenu && (
              <>
                <div onClick={() => setShowSettingsMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 900 }} />
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', minWidth: 190, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', boxShadow: '0 6px 20px rgba(0,0,0,0.35)', zIndex: 901, overflow: 'hidden' }}>
                  {employee?.is_admin && (
                    <>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowTeams(true); setShowSettingsMenu(false); }}>Teams</button>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowEmailTemplates(true); setShowSettingsMenu(false); }}>Email templates</button>
                      <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowShippingProfiles(true); setShowSettingsMenu(false); }}>Shipping profiles</button>
                    </>
                  )}
                  <button type="button" style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0, background: 'transparent', color: 'var(--text-primary)', padding: '0.5rem 0.9rem', cursor: 'pointer' }} onClick={() => { setShowMyPreferences(true); setShowSettingsMenu(false); }}>My preferences</button>
                </div>
              </>
            )}
          </div>
          <span className="current-user">{employee?.display_name}</span>
          <button type="button" className="btn-secondary" onClick={handleLogout}>Log out</button>
        </div>
      </div>
      <div id="main-content" tabIndex={-1} style={{ outline: 'none' }} />
      <PriorityAlertBanner alerts={activeAlerts} isAdmin={!!employee?.is_admin} onChanged={refreshAlerts} />
      {children}
      {showTeams && (
        <TeamManagerModal employees={employees} onClose={() => setShowTeams(false)} />
      )}
      {showEmailTemplates && (
        <EmailTemplateManagerModal onClose={() => setShowEmailTemplates(false)} />
      )}
      {showShippingProfiles && (
        <ShippingProfileManagerModal onClose={() => setShowShippingProfiles(false)} />
      )}
      {showUnmatchedEmails && (
        <UnmatchedEmailQueueModal onClose={() => setShowUnmatchedEmails(false)} />
      )}
      {showComposeEmail && (
        <ComposeBroadcastEmailModal onClose={() => setShowComposeEmail(false)} />
      )}
      {showCustomerEmails && (
        <CustomerEmailsModal onClose={() => setShowCustomerEmails(false)} />
      )}
      {showMyPreferences && (
        <MyPreferencesModal employee={employee} onClose={() => setShowMyPreferences(false)} onUpdated={updateEmployee} />
      )}
      {showDailyReport && (
        <DailyReportModal onClose={() => setShowDailyReport(false)} onSubmitted={() => { setDailyReportSubmitted(true); window.dispatchEvent(new Event('helpdesk-workday-refresh')); }} />
      )}
      {showAttendance && <AttendanceModal onClose={() => setShowAttendance(false)} />}
      {showDailyReportArchive && (
        <DailyReportArchiveModal employees={employees} isAdmin={!!employee?.is_admin} onClose={() => setShowDailyReportArchive(false)} />
      )}
      {showPostAlert && (
        <PostAlertModal onClose={() => setShowPostAlert(false)} onPosted={refreshAlerts} />
      )}
      {showChat && (
        <ChatModal
          employee={employee} employees={employees} teams={teams}
          onClose={() => setShowChat(false)} onUnreadChanged={refreshChatUnreadCount}
          openConversationId={jumpToConversationId}
          onOpenedConversation={() => setJumpToConversationId(null)}
        />
      )}
    </div>
  );
};

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/sso" element={<SsoLogin />} />
        <Route
          path="/board/:boardKey"
          element={
            <RequireAuth>
              <Shell><BoardPage /></Shell>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/board/SD" replace />} />
      </Routes>
      <ToastContainer position="bottom-right" theme="dark" />
    </BrowserRouter>
  </AuthProvider>
);

export default App;
